/**
 * Single owner of chart label/legend enforcement rules — the renderer-side pass that
 * makes table-stakes label quality hold no matter what a block's render_function_js
 * emits (agent-authored Chart.js configs hardcode label behavior inconsistently).
 *
 * All rules here are conservative "fill gaps / wrap, never override an explicit
 * choice" safeguards, mirroring the theming safeguards in dashboardHelpers.ts:
 *   - data labels never overlap or spill outside the chart area (display 'auto' + clamp),
 *   - a bar's value label never sits on top of its category label: a label that does not
 *     fit inside a short bar moves outside its tip, and a negative bar's label never walks
 *     into the axis (per-label placement resolved at render time from real geometry),
 *   - a label drawn outside the longest bar never clips at the canvas edge (pixel-exact
 *     layout padding — the widest label's text width on horizontal bars, one label-box
 *     height on vertical ones; clamp can't help there because the bar itself is fully
 *     visible),
 *   - a raw "1234.00" never renders as a data label (trailing zero-decimal strip),
 *   - data-label callbacks that read `context.raw` (absent from the datalabels plugin's
 *     context) get the data point instead of undefined,
 *   - long category/legend names truncate at the END with an ellipsis (tooltips carry
 *     the full value) so charts on the same page truncate identically,
 *   - the legend can never crowd out the plot (size cap proportional to the chart) and is
 *     never drawn inside it.
 *
 * `formatChartNumber` is the shared number formatting helper exposed to
 * render_function_js as an iframe global (blocks are prompted to route all label/tick
 * formatting through it). Keep this module dependency-light and DOM-free so every rule
 * is unit-testable; `createChart` calls `applyChartLabelEnforcement` with measured
 * container size. Add new label/legend rules here, not inline in createChart.
 */
import type { ChartOptions } from "./dashboardHelpers";

/** Category tick labels longer than this truncate at the end ("Acme Corporation In…"). */
export const MAX_AXIS_TICK_LABEL_CHARS = 24;
/**
 * Horizontal bars stack names down the y axis, where pixel width (not a shared character
 * count) is the constraint: a name stays whole while it fits in this share of the chart.
 */
export const HORIZONTAL_TICK_LABEL_MAX_WIDTH_FRACTION = 0.4;
/** Legend item text longer than this truncates at the end. */
export const MAX_LEGEND_LABEL_CHARS = 32;
// Legend caps as a fraction of the chart container, so a legend can never be huge
// relative to the plot. Floors keep at least one readable row/column on tiny cards.
const LEGEND_MAX_HEIGHT_FRACTION = 0.3;
const LEGEND_MAX_WIDTH_FRACTION = 0.35;
const LEGEND_MIN_HEIGHT_PX = 24;
const LEGEND_MIN_WIDTH_PX = 60;

// Chart types whose index axis is a category axis fed by `data.labels` — the axes we
// end-truncate. Radar/polar (r scale) and object-data types (scatter, matrix, treemap)
// are excluded: their tick semantics differ and blocks own their formatters there.
const CATEGORY_INDEX_AXIS_CHART_TYPES = new Set(["bar", "line"]);

// Chart types whose data points are plain numbers, where a default datalabels
// formatter of String(value) is faithful to what the plugin would render anyway.
// Object-data types (matrix, treemap, sankey, scatter…) are excluded — their blocks
// are required to bring their own formatter and we must not stringify objects.
const SCALAR_DATA_CHART_TYPES = new Set([
  "bar",
  "line",
  "pie",
  "doughnut",
  "polarArea",
  "funnel",
  "radar",
]);

// Room for datalabels drawn OUTSIDE a bar's tip. Sized in PIXELS — the widest label's
// text width beside a horizontal bar, one label box (Chart.js's default 12px font at 1.2
// line-height) above a vertical one — plus the datalabels plugin's default 4px offset and
// a small buffer, because the needed room is a pixel quantity; a range-proportional
// cushion (grace) under- or over-shoots depending on plot size.
const DATALABEL_DEFAULT_FONT_SIZE_PX = 12;
const DATALABEL_FONT_LINE_HEIGHT = 1.2;
const DATALABEL_DEFAULT_OFFSET_PX = 4;
const DATALABEL_EDGE_BUFFER_PX = 4;
// The plugin pads every label frame by 4px on each side. The frame is what its collision
// detection ('auto') compares, so on a 10-row horizontal bar chart the frames (12px text
// + 8px) outgrow the ~22px row pitch and every other value silently disappears. 2px keeps
// real overlaps detected without inventing phantom ones. Filled only for plain text labels
// (a block drawing label pills with a background/border owns its padding).
const DATALABEL_DEFAULT_FRAME_PADDING_PX = 2;
// A label counts as fitting inside a bar only with this much bar left over on each end.
const DATALABEL_INSIDE_FIT_MARGIN_PX = 4;
// Text-width estimate when no canvas measurer is available (unit tests): average glyph
// advance of a sans-serif face as a fraction of the font size, bold slightly wider.
const FALLBACK_CHAR_WIDTH_EM = 0.62;
const FALLBACK_BOLD_WIDTH_FACTOR = 1.06;
const MAX_MEASURED_LABELS = 500;
// Typical width of a vertical value axis (tick labels + padding), for the side-room estimate.
const VALUE_AXIS_WIDTH_ESTIMATE_PX = 56;

const COMPACT_TIERS: ReadonlyArray<readonly [number, string]> = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
];

export interface FormatChartNumberOptions {
  /** "currency" prefixes the symbol, "percent" suffixes "%" (value is the percent itself: 37.5 → "37.5%"). */
  style?: "number" | "currency" | "percent";
  /** Currency symbol for style "currency". Defaults to "$". */
  currency?: string;
  /** Max decimal places. Defaults to ~3 significant digits; trailing zeros always drop. */
  decimals?: number;
  /** K/M/B/T rounding for |value| >= 1000. Defaults to true; false → "2,548,411". */
  compact?: boolean;
  /** Prefix "+" on positive values (deltas): "+$2.55M". */
  signed?: boolean;
}

/** Round to at most `maxDecimals`, drop trailing zero decimals, add thousands separators. */
function trimNumber(value: number, maxDecimals: number): string {
  return Number(value.toFixed(maxDecimals)).toLocaleString("en-US", {
    maximumFractionDigits: maxDecimals,
  });
}

/** Default decimal budget: ~3 significant digits (255 → "255", 25.5 → "25.5", 2.55 → "2.55"). */
function defaultDecimals(abs: number): number {
  if (abs >= 100) {
    return 0;
  }
  if (abs >= 10) {
    return 1;
  }
  return 2;
}

/**
 * The one number formatting helper for chart labels, ticks, and tooltips (exposed to
 * render_function_js as an iframe global). Currency symbols, K/M rounding sized to
 * read like an axis ("$2.55M", not "$2,548,411"), and never a trailing ".00".
 * Non-numeric input passes through unchanged (null/undefined → "") so it is safe to
 * route every label through it.
 */
export function formatChartNumber(value: unknown, options: FormatChartNumberOptions = {}): string {
  const { style = "number", currency = "$", decimals, compact = true, signed = false } = options;

  let num: number;
  if (typeof value === "number") {
    num = value;
  } else if (typeof value === "string" && value.trim() !== "") {
    // Tolerate sheet-formatted numeric strings ("$1,234.00").
    num = Number(value.replace(/[,$\s]/g, ""));
  } else {
    num = NaN;
  }
  if (!Number.isFinite(num)) {
    return value == null ? "" : String(value);
  }

  const negative = num < 0;
  const abs = Math.abs(num);
  let body: string;

  if (style === "percent") {
    body = `${trimNumber(abs, decimals ?? 1)}%`;
  } else {
    let mantissa = abs;
    let suffix = "";
    if (compact) {
      for (let tier = 0; tier < COMPACT_TIERS.length; tier++) {
        const [threshold, tierSuffix] = COMPACT_TIERS[tier];
        if (abs >= threshold) {
          mantissa = abs / threshold;
          suffix = tierSuffix;
          // Rounding can overflow the tier (999,950 → "1000K"); bump to the next one.
          if (Number(mantissa.toFixed(decimals ?? defaultDecimals(mantissa))) >= 1000 && tier > 0) {
            const [upThreshold, upSuffix] = COMPACT_TIERS[tier - 1];
            mantissa = abs / upThreshold;
            suffix = upSuffix;
          }
          break;
        }
      }
    }
    // In compact mode `mantissa` is the abbreviated value (< 1000), so the ~3-sig-digit
    // budget reads like an axis ("2.55M"). In non-compact mode `mantissa` is the full
    // value, so that same budget drops cents on large numbers (1234.56 → "1,235"); keep
    // 2 decimals there so tooltips stay full precision (trailing zeros still drop).
    const maxDecimals =
      decimals ??
      (Number.isInteger(mantissa) ? 0 : compact ? defaultDecimals(Math.abs(mantissa)) : 2);
    body = `${trimNumber(mantissa, maxDecimals)}${suffix}`;
    if (style === "currency") {
      body = `${currency}${body}`;
    }
  }

  const sign = negative ? "-" : signed && num > 0 ? "+" : "";
  return `${sign}${body}`;
}

/**
 * End-truncation with an ellipsis — never truncate the front, so rows on different
 * charts can still be matched by their leading characters. Non-strings pass through.
 */
export function truncateChartLabel(label: string, maxChars = MAX_AXIS_TICK_LABEL_CHARS): string {
  if (typeof label !== "string" || label.length <= maxChars) {
    return label;
  }
  return `${label.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

/**
 * Strip an ALL-ZERO decimal tail inherited from raw sheet formatting ("1,234.00" →
 * "1,234", "$10.00M" → "$10M"). Non-zero decimals are meaningful and untouched
 * ("2.55M", "2025.10"), so this can safely wrap any label the block emits — including
 * ones that concatenate arbitrary category text: the zeros must terminate the numeric
 * token (no digit/./, after), so dotted identifiers ("v2.0.1", "192.168.0.1") and
 * locale-grouped numbers ("1.000,50") pass through intact.
 */
export function stripTrailingZeroDecimals(label: string): string {
  return label.replace(/(\d)\.0+(?![\d.,])/g, "$1");
}

/** stripTrailingZeroDecimals over a datalabels formatter result (string or multi-line array). */
function stripLabelResult(result: unknown): unknown {
  if (typeof result === "string") {
    return stripTrailingZeroDecimals(result);
  }
  if (Array.isArray(result)) {
    return result.map(line => (typeof line === "string" ? stripTrailingZeroDecimals(line) : line));
  }
  return result;
}

type LegendItemLike = { text?: unknown };

/** Truncate legend item text at the end; items keep every other property untouched. */
export function truncateLegendItems<T extends LegendItemLike | null | undefined>(
  items: T[] | null | undefined
): T[] {
  return (items ?? []).map(item =>
    item && typeof item.text === "string" && item.text.length > MAX_LEGEND_LABEL_CHARS
      ? { ...item, text: truncateChartLabel(item.text, MAX_LEGEND_LABEL_CHARS) }
      : item
  );
}

export interface ChartContainerSize {
  width: number;
  height: number;
  /**
   * Text width in CSS px for a CSS font string ("normal bold 11px Inter, sans-serif");
   * createChart passes the canvas 2d measurer. Omitted (unit tests, unmeasurable), widths
   * are estimated from the font size.
   */
  measureText?: (text: string, font: string) => number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

/**
 * The label enforcement pass `createChart` runs on every chart config (after theming,
 * before constructing the Chart). Mutates `chartOptions` in place, like
 * applyThemeToChartDatasets. `container` is the measured `.chart-container` size used
 * for the legend cap; omit when unmeasurable (hidden/zero-size) and the cap is skipped.
 */
export function applyChartLabelEnforcement(
  chartOptions: ChartOptions,
  container?: ChartContainerSize
): void {
  const options = (chartOptions.options ||= {}) as AnyRecord;
  const plugins = (options.plugins ||= {}) as AnyRecord;

  enforceDatalabels(chartOptions, plugins);
  enforceBarLabelPlacement(chartOptions, options, plugins);
  enforceOutsideLabelRoom(chartOptions, options, plugins, container);
  enforceAxisTickTruncation(chartOptions, options, container);
  enforceLegend(chartOptions, plugins, container);
}

/**
 * Data labels never overlap ('auto' hides a label that would collide with another, over a
 * frame padded just enough to detect real collisions), never spill outside the chart area
 * (clamp), and never render a raw ".00" tail — at the chart level and on every dataset's
 * own datalabels. Explicit block choices are kept (see upgradeDatalabelConfig).
 */
function enforceDatalabels(chartOptions: ChartOptions, plugins: AnyRecord): void {
  const chartLevel = plugins.datalabels as AnyRecord | undefined;
  if (chartLevel && typeof chartLevel === "object") {
    upgradeDatalabelConfig(chartLevel);
    const formatter = chartLevel.formatter;
    if (
      formatter === undefined &&
      SCALAR_DATA_CHART_TYPES.has(chartOptions.type) &&
      !hasPlainObjectDataPoints(chartOptions)
    ) {
      // Same output the plugin default would render for scalar/array data, minus the
      // ".00" tail. (For arrays like a waterfall's [start, end], String matches the
      // plugin's "" + value too.)
      chartLevel.formatter = (value: unknown) =>
        value == null ? null : stripLabelResult(String(value));
    }
  }
  // Dataset-level datalabels (a line series' own labels on a bar+line chart) override the
  // chart-level ones key by key in the plugin, so they need the same upgrade or their
  // labels keep colliding while the chart-level ones don't.
  for (const dataset of chartOptions.data?.datasets ?? []) {
    const own = (dataset as AnyRecord).datalabels as AnyRecord | undefined;
    if (own && typeof own === "object") {
      upgradeDatalabelConfig(own);
    }
  }
}

/**
 * The per-config half of enforceDatalabels: collision-aware display, clamp, a tight
 * frame, and a ".00"-stripping formatter wrap. Explicit choices (display: false, a
 * scriptable display, clamp, padding, a formatter) are kept — functions are wrapped,
 * values only filled when unset.
 */
function upgradeDatalabelConfig(datalabels: AnyRecord): void {
  provideRawOnScriptableContexts(datalabels);

  const display = datalabels.display;
  if (display === undefined || display === true) {
    datalabels.display = "auto";
  } else if (typeof display === "function") {
    // Keep the block's threshold logic; upgrade its "show" verdict to collision-aware.
    datalabels.display = (...args: unknown[]) => {
      const result = display(...args);
      return result === true ? "auto" : result;
    };
  }

  if (datalabels.clamp === undefined) {
    datalabels.clamp = true;
  }
  if (
    datalabels.padding === undefined &&
    datalabels.backgroundColor === undefined &&
    datalabels.borderWidth === undefined
  ) {
    datalabels.padding = DATALABEL_DEFAULT_FRAME_PADDING_PX;
  }

  const formatter = datalabels.formatter;
  if (typeof formatter === "function") {
    datalabels.formatter = (...args: unknown[]) => stripLabelResult(formatter(...args));
  }
}

// Datalabels options whose callbacks receive the plugin context as an argument other than
// the first (`formatter(value, context)`); every other scriptable option is `(context)`.
const DATALABEL_CONTEXT_ARG_INDEX: Record<string, number> = { formatter: 1 };
// Non-scriptable function-valued datalabels keys: event handlers, not option resolvers.
const DATALABEL_NON_SCRIPTABLE_KEYS = new Set(["listeners"]);

/**
 * Chart.js's own scriptable contexts carry `raw` (the data point), but the datalabels
 * plugin's context does not — only chart/dataset/dataIndex/datasetIndex/active. Blocks
 * routinely read `context.raw` anyway (it's what every other Chart.js callback offers),
 * which silently resolves to undefined: a `display: ctx => !!ctx.raw?.name` hides every
 * label and a `formatter: (v, ctx) => ctx.raw?.name` prints nothing. Wrap each scriptable
 * datalabels option so its context gets `raw = dataset.data[dataIndex]` when missing, and
 * do the same for each named label in a multi-label `labels` config.
 */
function provideRawOnScriptableContexts(datalabels: AnyRecord): void {
  for (const [key, value] of Object.entries(datalabels)) {
    if (typeof value === "function" && !DATALABEL_NON_SCRIPTABLE_KEYS.has(key)) {
      datalabels[key] = withRawContext(value, DATALABEL_CONTEXT_ARG_INDEX[key] ?? 0);
    }
  }
  const labels = datalabels.labels;
  if (labels && typeof labels === "object") {
    for (const label of Object.values(labels as AnyRecord)) {
      if (label && typeof label === "object") {
        provideRawOnScriptableContexts(label as AnyRecord);
      }
    }
  }
}

function withRawContext(
  fn: (...args: unknown[]) => unknown,
  contextIndex: number
): (...args: unknown[]) => unknown {
  return (...args: unknown[]) => {
    const context = args[contextIndex] as AnyRecord | undefined;
    if (context && typeof context === "object" && !("raw" in context)) {
      const data = (context.dataset as AnyRecord | undefined)?.data;
      if (Array.isArray(data) && typeof context.dataIndex === "number") {
        context.raw = data[context.dataIndex];
      }
    }
    return fn(...args);
  };
}

/**
 * True when a dataset carries plain-object data points ({x, y}, {label, r}) — legal on
 * bar/line. The plugin's default formatter expands those (.label, .r, "k: v" pairs);
 * String(value) would render "[object Object]", so the default must be left alone.
 */
function hasPlainObjectDataPoints(chartOptions: ChartOptions): boolean {
  return (chartOptions.data?.datasets ?? []).some(dataset => {
    const data = (dataset as AnyRecord).data;
    if (!Array.isArray(data)) {
      return false;
    }
    const firstValue = data.find(value => value != null);
    return typeof firstValue === "object" && !Array.isArray(firstValue);
  });
}

/** Datalabels `align` values that point along the bar's orientation vector. */
type BarAlign = "start" | "end" | "center" | "left" | "right" | "top" | "bottom";

export interface BarLabelPlacement {
  anchor: string;
  align: string;
}

export interface BarLabelPlacementInput {
  /** The block's own placement for this label (its static values or scriptable results). */
  base: BarLabelPlacement;
  horizontal: boolean;
  /** Bar tip pixel position on the value axis (Chart.js element `x`/`y`). */
  tip: number;
  /** Bar base pixel position on the value axis (Chart.js element `base`). */
  basePixel: number;
  /** Label size along the value axis: text width + frame padding (horizontal) or text height + padding (vertical). */
  labelLength: number;
  offset: number;
  /** Chart area extent along the value axis, and the layout padding beyond it on each end. */
  areaStart: number;
  areaEnd: number;
  paddingBeforeStart: number;
  paddingAfterEnd: number;
}

/**
 * Where a bar's value label goes, from real geometry. The datalabels plugin orients bar
 * anchors purely geometrically — on horizontal bars 'end' is always the RIGHT edge and
 * 'start' the LEFT edge whatever the sign (vertical: 'end' = top, 'start' = bottom) — so a
 * block's static "anchor 'end', align 'start'" (inside, from the tip) overflows a short
 * bar's left end onto the category label, and its outside-left label on a negative bar
 * walks into the axis. Rules, in order:
 *   1. an inside placement the label fits in is kept;
 *   2. otherwise the label sits outside the bar's tip when that stays on the canvas (the
 *      positive side has layout padding reserved for exactly this);
 *   3. a negative bar whose tip side would cross the category axis puts the label on the
 *      base side (beside the zero line, away from the axis) when that fits;
 *   4. else inside the bar if it fits, else the block's placement as a last resort.
 * A block's outside-at-the-tip placement that already fits is returned unchanged.
 */
export function resolveBarLabelPlacement(input: BarLabelPlacementInput): BarLabelPlacement {
  const { base, horizontal, tip, basePixel, labelLength, offset } = input;
  // Along the value axis, "outward" is +x for horizontal bars and -y (up) for vertical ones.
  const positive = horizontal ? tip >= basePixel : tip <= basePixel;
  const barLength = Math.abs(tip - basePixel);
  const need = labelLength + offset;
  const anchor = base.anchor;
  const align = base.align as BarAlign;
  const alignsOutward = horizontal
    ? align === "end" || align === "right"
    : align === "end" || align === "top";
  const alignsInward = horizontal
    ? align === "start" || align === "left"
    : align === "start" || align === "bottom";
  // Named placements in plugin terms (horizontal / vertical).
  const outsideTipPositive: BarLabelPlacement = horizontal
    ? { anchor: "end", align: "right" }
    : { anchor: "end", align: "top" };
  const outsideTipNegative: BarLabelPlacement = horizontal
    ? { anchor: "start", align: "left" }
    : { anchor: "start", align: "bottom" };
  const insideFromTipPositive: BarLabelPlacement = horizontal
    ? { anchor: "end", align: "left" }
    : { anchor: "end", align: "bottom" };
  const insideFromTipNegative: BarLabelPlacement = horizontal
    ? { anchor: "start", align: "right" }
    : { anchor: "start", align: "top" };

  const blockPlacesOutsideTip = positive
    ? anchor === "end" && alignsOutward
    : anchor === "start" && alignsInward;
  const blockPlacesOutsideBase = positive
    ? anchor === "start" && alignsInward
    : anchor === "end" && alignsOutward;
  const blockPlacesInside = !blockPlacesOutsideTip && !blockPlacesOutsideBase;
  const fitsInside = barLength >= labelLength + 2 * DATALABEL_INSIDE_FIT_MARGIN_PX;

  if (blockPlacesInside && fitsInside) {
    return base;
  }
  // Outside the tip, staying on the canvas. For a horizontal bar the value axis runs
  // start→end left→right; for a vertical bar the pixel axis runs top→bottom, so the
  // "outward" end of a positive bar is toward areaStart (the top).
  const outsideTipFits = horizontal
    ? positive
      ? tip + need <= input.areaEnd + input.paddingAfterEnd
      : tip - need >= input.areaStart
    : positive
    ? tip - need >= input.areaStart - input.paddingBeforeStart
    : tip + need <= input.areaEnd;
  if (outsideTipFits) {
    return blockPlacesOutsideTip ? base : positive ? outsideTipPositive : outsideTipNegative;
  }
  if (!positive) {
    // Beside the zero line, on the side away from the category axis.
    const baseSideFits = horizontal
      ? basePixel + need <= input.areaEnd + input.paddingAfterEnd
      : basePixel - need >= input.areaStart - input.paddingBeforeStart;
    if (baseSideFits) {
      return horizontal ? { anchor: "end", align: "right" } : { anchor: "end", align: "top" };
    }
  }
  if (fitsInside) {
    return positive ? insideFromTipPositive : insideFromTipNegative;
  }
  return base;
}

/** Datalabels-style scriptable resolution: functions get (context, index). */
function resolveScriptable(value: unknown, context: AnyRecord): unknown {
  return typeof value === "function" ? value(context, context.dataIndex) : value;
}

/** A formatter result as text lines, the way the plugin renders it. */
function toTextLines(value: unknown): string[] {
  if (value == null) {
    return [];
  }
  const values = Array.isArray(value) ? value : [value];
  const lines: string[] = [];
  for (const item of values) {
    if (item == null) {
      continue;
    }
    lines.push(...String(item).split("\n"));
  }
  return lines;
}

interface ResolvedLabelFont {
  size: number;
  weight: string;
  style: string;
  family: string;
  lineHeight: number;
}

function resolveLabelFont(fontOption: unknown, chartFont?: AnyRecord): ResolvedLabelFont {
  const font = (fontOption && typeof fontOption === "object" ? fontOption : {}) as AnyRecord;
  const size =
    typeof font.size === "number"
      ? font.size
      : typeof chartFont?.size === "number"
      ? chartFont.size
      : DATALABEL_DEFAULT_FONT_SIZE_PX;
  const lineHeight =
    typeof font.lineHeight === "number"
      ? font.lineHeight * size
      : Math.ceil(size * DATALABEL_FONT_LINE_HEIGHT * 10) / 10;
  return {
    size,
    weight: String(font.weight ?? chartFont?.weight ?? "normal"),
    style: String(font.style ?? chartFont?.style ?? "normal"),
    family: String(font.family ?? chartFont?.family ?? "sans-serif"),
    lineHeight,
  };
}

function fontString(font: ResolvedLabelFont): string {
  return `${font.style} ${font.weight} ${font.size}px ${font.family}`;
}

function estimateTextWidth(text: string, font: ResolvedLabelFont): number {
  const weight = font.weight;
  const bold = weight === "bold" || weight === "bolder" || Number(weight) >= 600;
  return text.length * font.size * FALLBACK_CHAR_WIDTH_EM * (bold ? FALLBACK_BOLD_WIDTH_FACTOR : 1);
}

function measureLinesWidth(
  lines: string[],
  font: ResolvedLabelFont,
  measureText?: (text: string, font: string) => number
): number {
  const css = fontString(font);
  let widest = 0;
  for (const line of lines) {
    let width: number | undefined;
    if (measureText) {
      try {
        width = measureText(line, css);
      } catch {
        width = undefined;
      }
    }
    if (typeof width !== "number" || !Number.isFinite(width)) {
      width = estimateTextWidth(line, font);
    }
    widest = Math.max(widest, width);
  }
  return widest;
}

/** Sum of the plugin's frame padding along one axis (number or {top,right,bottom,left}). */
function framePadding(padding: unknown, axis: "x" | "y"): number {
  if (typeof padding === "number") {
    return padding * 2;
  }
  if (padding && typeof padding === "object") {
    const p = padding as AnyRecord;
    return axis === "x"
      ? Number(p.left ?? 0) + Number(p.right ?? 0)
      : Number(p.top ?? 0) + Number(p.bottom ?? 0);
  }
  return DATALABEL_DEFAULT_FRAME_PADDING_PX * 2;
}

/** Label text for a value through the (already wrapped) block formatter; String(value) on failure. */
function labelLinesFor(formatter: unknown, value: unknown, context: AnyRecord): string[] {
  if (typeof formatter === "function") {
    try {
      return toTextLines(formatter(value, context));
    } catch {
      // A formatter that needs live chart state falls back to the raw value.
    }
  }
  return toTextLines(value);
}

/** Chart.js layout padding as four sides (number, partial object, or unset). */
function layoutPaddingSides(padding: unknown): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  if (typeof padding === "number") {
    return { top: padding, right: padding, bottom: padding, left: padding };
  }
  const p = (padding && typeof padding === "object" ? padding : {}) as AnyRecord;
  return {
    top: Number(p.top ?? 0) || 0,
    right: Number(p.right ?? 0) || 0,
    bottom: Number(p.bottom ?? 0) || 0,
    left: Number(p.left ?? 0) || 0,
  };
}

function isBarDataset(chartOptions: ChartOptions, dataset: AnyRecord): boolean {
  const type = dataset.type ?? chartOptions.type;
  return type === "bar" || type === "horizontalBar";
}

function isStackedBarChart(chartOptions: ChartOptions, options: AnyRecord): boolean {
  const scales = (options.scales as AnyRecord) ?? {};
  if (Object.values(scales).some(scale => (scale as AnyRecord | null)?.stacked === true)) {
    return true;
  }
  return (chartOptions.data?.datasets ?? []).some(dataset => (dataset as AnyRecord).stack != null);
}

/** Per-dataset datalabels config the plugin would use: chart-level options under dataset-level ones. */
function datasetLabelConfig(plugins: AnyRecord, dataset: AnyRecord): AnyRecord | undefined {
  const chartLevel = plugins.datalabels as AnyRecord | undefined;
  const datasetLevel = dataset.datalabels as AnyRecord | undefined;
  if (!chartLevel && !datasetLevel) {
    return undefined;
  }
  return { ...(chartLevel ?? {}), ...(datasetLevel ?? {}) };
}

/**
 * Bar value labels are placed per label at render time from the real bar geometry (see
 * resolveBarLabelPlacement): the block's anchor/align stay the starting point and are kept
 * whenever they don't collide. Installed on the chart-level datalabels options and on any
 * dataset-level ones that set their own anchor/align; the plugin merges chart-level under
 * dataset-level, so a dataset without its own keys inherits the wrapped chart-level pair.
 * Skipped for stacked bars (a middle segment's tip is the next segment, not free space),
 * rotated labels, and non-numeric data points (floating [start, end] bars, objects).
 */
function enforceBarLabelPlacement(
  chartOptions: ChartOptions,
  options: AnyRecord,
  plugins: AnyRecord
): void {
  if (chartOptions.type !== "bar" && chartOptions.type !== "horizontalBar") {
    return;
  }
  if (isStackedBarChart(chartOptions, options)) {
    return;
  }
  const chartLevelRaw = plugins.datalabels as AnyRecord | undefined;
  const chartLevel = chartLevelRaw && typeof chartLevelRaw === "object" ? chartLevelRaw : undefined;
  const horizontal = options.indexAxis === "y" || chartOptions.type === "horizontalBar";
  const install = (target: AnyRecord, fallback: AnyRecord): void => {
    const base = {
      anchor: target.anchor ?? fallback.anchor,
      align: target.align ?? fallback.align,
    };
    const decide = (context: AnyRecord): BarLabelPlacement =>
      placeBarLabel(context, base, plugins, horizontal);
    target.anchor = (context: AnyRecord) => decide(context).anchor;
    target.align = (context: AnyRecord) => decide(context).align;
  };
  // The block's chart-level pair, captured before wrapping, is the fallback for datasets
  // that set their own datalabels without anchor/align.
  const chartBase = { anchor: chartLevel?.anchor, align: chartLevel?.align };
  if (chartLevel && chartLevel.display !== false && chartLevel.rotation == null) {
    install(chartLevel, {});
  }
  // A dataset's own datalabels win key by key in the plugin, so they need their own wrapper —
  // also when the chart level has none or hides labels (a bar+line chart that disables labels
  // globally and re-enables them on the bar series).
  for (const dataset of chartOptions.data?.datasets ?? []) {
    const own = (dataset as AnyRecord).datalabels as AnyRecord | undefined;
    if (
      own &&
      typeof own === "object" &&
      own.display !== false &&
      (own.rotation ?? chartLevel?.rotation) == null &&
      isBarDataset(chartOptions, dataset as AnyRecord)
    ) {
      install(own, chartBase);
    }
  }
}

/** Runtime adapter: the plugin context → geometry → resolveBarLabelPlacement. */
function placeBarLabel(
  context: AnyRecord,
  base: { anchor: unknown; align: unknown },
  plugins: AnyRecord,
  horizontal: boolean
): BarLabelPlacement {
  const resolved: BarLabelPlacement = {
    anchor: String(resolveScriptable(base.anchor, context) ?? "center"),
    align: String(resolveScriptable(base.align, context) ?? "center"),
  };
  const chart = context.chart as AnyRecord | undefined;
  const dataset = context.dataset as AnyRecord | undefined;
  if (!chart || !dataset) {
    return resolved;
  }
  const value = (dataset.data as unknown[] | undefined)?.[context.dataIndex];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return resolved;
  }
  // The text/font/offset the plugin will use for THIS dataset (dataset-level over chart-level).
  const config = datasetLabelConfig(plugins, dataset) ?? {};
  const meta =
    typeof chart.getDatasetMeta === "function"
      ? chart.getDatasetMeta(context.datasetIndex)
      : undefined;
  const element = meta?.data?.[context.dataIndex];
  const area = chart.chartArea as AnyRecord | undefined;
  if (!element || typeof element.getProps !== "function" || meta.type !== "bar" || !area) {
    return resolved;
  }
  const props = element.getProps(["x", "y", "base"], true) as AnyRecord;
  const tip = horizontal ? props.x : props.y;
  const basePixel = props.base;
  if (typeof tip !== "number" || typeof basePixel !== "number") {
    return resolved;
  }
  const lines = labelLinesFor(config.formatter, value, context);
  if (lines.length === 0) {
    return resolved;
  }
  const font = resolveLabelFont(resolveScriptable(config.font, context), chart.options?.font);
  const canvasContext = chart.ctx as CanvasRenderingContext2D | undefined;
  const measure = canvasContext
    ? (text: string, css: string): number => {
        canvasContext.save();
        canvasContext.font = css;
        const width = canvasContext.measureText(text).width;
        canvasContext.restore();
        return width;
      }
    : undefined;
  const padding = resolveScriptable(config.padding, context);
  const labelLength = horizontal
    ? measureLinesWidth(lines, font, measure) + framePadding(padding, "x")
    : font.lineHeight * lines.length + framePadding(padding, "y");
  const offsetValue = resolveScriptable(config.offset, context);
  const offset = typeof offsetValue === "number" ? offsetValue : DATALABEL_DEFAULT_OFFSET_PX;
  const layoutPadding = layoutPaddingSides(chart.options?.layout?.padding);
  return resolveBarLabelPlacement({
    base: resolved,
    horizontal,
    tip,
    basePixel,
    labelLength,
    offset,
    areaStart: horizontal ? area.left : area.top,
    areaEnd: horizontal ? area.right : area.bottom,
    paddingBeforeStart: horizontal ? layoutPadding.left : layoutPadding.top,
    paddingAfterEnd: horizontal ? layoutPadding.right : layoutPadding.bottom,
  });
}

/**
 * Layout padding on the side where labels leave the plot, so the label beside the longest
 * bar always has canvas room — clamp is a no-op there (it only pulls the anchor inside the
 * VISIBLE part of the element, and that bar is fully visible). Horizontal bars reserve the
 * RIGHT side, sized to the widest label the block's formatter produces (positive tips and
 * a negative bar's base-side fallback both extend right); vertical bars and line charts
 * with above-the-point labels reserve the TOP, one label box. Every bar chart with value
 * labels gets it — a label that does not fit inside its bar moves outside at render time
 * (enforceBarLabelPlacement), so inside placements need the room too. A block's own
 * padding or value-axis grace is a floor, not an opt-out: the room is the larger of the two.
 */
function enforceOutsideLabelRoom(
  chartOptions: ChartOptions,
  options: AnyRecord,
  plugins: AnyRecord,
  container?: ChartContainerSize
): void {
  if (
    !CATEGORY_INDEX_AXIS_CHART_TYPES.has(chartOptions.type) &&
    chartOptions.type !== "horizontalBar"
  ) {
    return;
  }
  const datasets = (chartOptions.data?.datasets ?? []) as AnyRecord[];
  const horizontal = options.indexAxis === "y" || chartOptions.type === "horizontalBar";
  const chartFont = options.font as AnyRecord | undefined;

  let needed = 0;
  let widestVerticalLabel = 0;
  for (const dataset of datasets) {
    const config = datasetLabelConfig(plugins, dataset);
    if (!config || config.display === false) {
      continue;
    }
    const isBar = isBarDataset(chartOptions, dataset);
    if (!isBar) {
      // Points/lines: only a static above-the-element placement provably leaves the plot.
      const drawsAbove =
        !horizontal &&
        config.anchor === "end" &&
        (config.align === "top" || config.align === "end");
      if (!drawsAbove) {
        continue;
      }
    }
    const font = resolveLabelFont(config.font, chartFont);
    const offset = typeof config.offset === "number" ? config.offset : DATALABEL_DEFAULT_OFFSET_PX;
    const values = Array.isArray(dataset.data) ? (dataset.data as unknown[]) : [];
    let widest = 0;
    let mostLines = 1;
    let measured = 0;
    for (let index = 0; index < values.length && measured < MAX_MEASURED_LABELS; index++) {
      const value = values[index];
      if (typeof value !== "number" || !Number.isFinite(value)) {
        continue;
      }
      measured++;
      const lines = labelLinesFor(config.formatter, value, {
        dataset,
        dataIndex: index,
        datasetIndex: datasets.indexOf(dataset),
        active: false,
      });
      if (lines.length === 0) {
        continue;
      }
      mostLines = Math.max(mostLines, lines.length);
      widest = Math.max(widest, measureLinesWidth(lines, font, container?.measureText));
    }
    if (measured === 0 && isBar) {
      continue; // Floating/object data — the block owns its labels there.
    }
    const room = horizontal
      ? widest + offset + DATALABEL_EDGE_BUFFER_PX
      : Math.ceil(font.lineHeight * mostLines) + offset + DATALABEL_EDGE_BUFFER_PX;
    needed = Math.max(needed, room);
    if (!horizontal && isBar) {
      widestVerticalLabel = Math.max(widestVerticalLabel, widest);
    }
  }
  if (needed <= 0) {
    return;
  }

  const layout = (options.layout ||= {}) as AnyRecord;
  const side = horizontal ? "right" : "top";
  if (typeof layout.padding === "number") {
    const uniform = layout.padding;
    layout.padding = { top: uniform, right: uniform, bottom: uniform, left: uniform };
  }
  const padding = (layout.padding ||= {}) as AnyRecord;
  if (typeof padding !== "object") {
    return;
  }
  const existing = typeof padding[side] === "number" ? padding[side] : 0;
  padding[side] = Math.max(existing, Math.ceil(needed));

  // A vertical bar's label is centered on the bar, so on the first/last bar it sticks out
  // half its width past the bar's half-category slot; on a narrow chart with few bars that
  // crosses the canvas edge. Reserve the shortfall on both sides, estimated from the
  // container width (minus a typical value-axis width) and the category count.
  const categories = chartOptions.data?.labels?.length ?? 0;
  if (
    !horizontal &&
    widestVerticalLabel > 0 &&
    categories > 0 &&
    container &&
    container.width > 0
  ) {
    const plotWidth = Math.max(0, container.width - VALUE_AXIS_WIDTH_ESTIMATE_PX);
    const halfCategory = plotWidth / categories / 2;
    const shortfall = Math.ceil(widestVerticalLabel / 2 + DATALABEL_EDGE_BUFFER_PX - halfCategory);
    if (shortfall > 0) {
      for (const edge of ["left", "right"]) {
        const current = typeof padding[edge] === "number" ? padding[edge] : 0;
        padding[edge] = Math.max(current, shortfall);
      }
    }
  }
}

/**
 * End-truncate long names on category axes so every chart truncates the same way;
 * the tooltip title carries the full name on hover. Applies to the index axis of
 * bar/line charts with string labels and to any axis explicitly typed "category".
 * A block's own ticks.callback is kept and its string output truncated. The y index
 * axis of a measured horizontal bar chart truncates by width instead (ZD #44103).
 */
function enforceAxisTickTruncation(
  chartOptions: ChartOptions,
  options: AnyRecord,
  container?: ChartContainerSize
): void {
  const labels = chartOptions.data?.labels;
  const hasStringLabels = Array.isArray(labels) && labels.some(l => typeof l === "string");

  const axisIds = new Set<string>();
  if (hasStringLabels && CATEGORY_INDEX_AXIS_CHART_TYPES.has(chartOptions.type)) {
    axisIds.add(options.indexAxis === "y" ? "y" : "x");
  }
  for (const [axisId, axis] of Object.entries((options.scales as AnyRecord) ?? {})) {
    if (axis != null && (axis as AnyRecord).type === "category") {
      axisIds.add(axisId);
    }
  }
  if (axisIds.size === 0) {
    return;
  }

  const scales = (options.scales ||= {}) as AnyRecord;
  for (const axisId of axisIds) {
    const axis = (scales[axisId] ||= {}) as AnyRecord;
    if (axis.type != null && axis.type !== "category") {
      continue; // Explicitly non-category (time/linear) — numeric ticks, nothing to truncate.
    }
    const ticks = (axis.ticks ||= {}) as AnyRecord;
    const truncate =
      axisId === "y" && options.indexAxis === "y" && container && container.width > 0
        ? widthTruncator(
            container.width * HORIZONTAL_TICK_LABEL_MAX_WIDTH_FRACTION,
            resolveLabelFont(ticks.font, options.font),
            container.measureText
          )
        : (label: string) => truncateChartLabel(label);
    const original = ticks.callback;
    if (typeof original === "function") {
      ticks.callback = function (this: unknown, ...args: unknown[]) {
        const result = original.apply(this, args);
        return typeof result === "string"
          ? truncate(result)
          : Array.isArray(result)
          ? result.map(line => (typeof line === "string" ? truncate(line) : line))
          : result;
      };
    } else {
      ticks.callback = function (
        this: { getLabelForValue?: (value: never) => string },
        value: unknown
      ) {
        const label =
          typeof this?.getLabelForValue === "function"
            ? this.getLabelForValue(value as never)
            : value;
        return typeof label === "string" ? truncate(label) : label;
      };
    }
  }
}

/**
 * End-truncate to `maxWidth` px, but never shorter than the shared character cap, so a
 * narrow card still reads like every other chart. Memoized: ticks re-render on every update.
 */
function widthTruncator(
  maxWidth: number,
  font: ResolvedLabelFont,
  measureText?: (text: string, font: string) => number
): (label: string) => string {
  const cache = new Map<string, string>();
  const fits = (text: string) => measureLinesWidth([text], font, measureText) <= maxWidth;
  return label => {
    const cached = cache.get(label);
    if (cached !== undefined) {
      return cached;
    }
    let result = label;
    if (label.length > MAX_AXIS_TICK_LABEL_CHARS && !fits(label)) {
      // Longest prefix whose "prefix…" fits, searched above the character-cap floor.
      let lo = MAX_AXIS_TICK_LABEL_CHARS - 1;
      let hi = label.length - 1;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (fits(`${label.slice(0, mid).trimEnd()}…`)) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }
      result = `${label.slice(0, lo).trimEnd()}…`;
    }
    cache.set(label, result);
    return result;
  };
}

/**
 * The legend never sits on the plot: Chart.js's `position: "chartArea"` draws it over the
 * data, so it moves to the top. Then cap the legend box proportional to the chart (Chart.js
 * otherwise lets a many-item legend consume most of the plot). The cap is fill-when-unset —
 * an explicit maxHeight/maxWidth or display: false is respected.
 */
function enforceLegend(
  chartOptions: ChartOptions,
  plugins: AnyRecord,
  container?: ChartContainerSize
): void {
  const legend = (plugins.legend ||= {}) as AnyRecord;
  if (legend.display === false) {
    return;
  }
  if (legend.position === "chartArea") {
    legend.position = "top";
  }
  if (!container || container.width <= 0 || container.height <= 0) {
    return;
  }
  const position = legend.position ?? "top";
  if (position === "left" || position === "right") {
    if (legend.maxWidth == null) {
      legend.maxWidth = Math.max(
        LEGEND_MIN_WIDTH_PX,
        Math.round(container.width * LEGEND_MAX_WIDTH_FRACTION)
      );
    }
  } else if (legend.maxHeight == null) {
    legend.maxHeight = Math.max(
      LEGEND_MIN_HEIGHT_PX,
      Math.round(container.height * LEGEND_MAX_HEIGHT_FRACTION)
    );
  }
}
